import { T } from '../styles/tokens';
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { user } from '../stores/auth.store';
import { route } from 'preact-router';
import { Button, Spinner, Textarea } from '../components/ui';

// Capture console errors
const capturedErrors: string[] = [];
if (typeof window !== 'undefined') {
  const origOnError = window.onerror;
  window.onerror = (msg, source, line, col, err) => {
    capturedErrors.push(`${msg} (${source}:${line}:${col})`);
    if (capturedErrors.length > 10) capturedErrors.shift();
    if (origOnError) return (origOnError as any)(msg, source, line, col, err);
    return false;
  };
  window.addEventListener('unhandledrejection', (e) => {
    capturedErrors.push(`Unhandled rejection: ${e.reason}`);
    if (capturedErrors.length > 10) capturedErrors.shift();
  });
}

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Critica' },
];

const SEVERITY_COLORS: Record<string, string> = {
  low: T.textMuted,
  medium: T.warning,
  high: T.warning,
  critical: T.danger,
};

export function BugReportPage(_props: { path?: string }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-captured info
  const [autoInfo] = useState(() => ({
    browser: navigator.userAgent,
    url: document.referrer || window.location.href,
    plan: '',
    resolution: `${window.screen.width}x${window.screen.height}`,
    timestamp: new Date().toISOString(),
    consoleErrors: [...capturedErrors],
  }));

  useEffect(() => {
    api.getQuiet<any>('/subscription/me').then(r => {
      if (r?.plan?.name) autoInfo.plan = r.plan.name;
      else if (r?.subscription?.plan) autoInfo.plan = r.subscription.plan;
    }).catch(() => {});
  }, []);

  const handleScreenshot = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onload = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleScreenshot(file);
  };

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Titulo e obrigatorio'); return; }
    if (!description.trim()) { setError('Descricao e obrigatoria'); return; }

    setSubmitting(true);
    setError('');

    try {
      await api.post('/admin/bug-reports', {
        title: title.trim(),
        description: description.trim(),
        steps_to_reproduce: steps.trim() || undefined,
        severity,
        browser_info: autoInfo.browser,
        page_url: autoInfo.url,
        user_plan: autoInfo.plan,
        screen_resolution: autoInfo.resolution,
        console_errors: autoInfo.consoleErrors.length > 0 ? JSON.stringify(autoInfo.consoleErrors) : '',
        screenshot_url: screenshotPreview || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Erro ao enviar bug report');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: T.bg,
    border: `1px solid ${T.whiteAlpha8}`, borderRadius: 8, color: T.text,
    fontSize: 14, outline: 'none', boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s',
  };

  if (success) {
    return (
      <div style={{ padding: '60px 24px', maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}>
          <circle cx="12" cy="12" r="10"/><polyline points="16 8 10 16 7 13"/>
        </svg>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: '0 0 8px' }}>Bug reportado com sucesso</h2>
        <p style={{ fontSize: 14, color: T.textSecondary, margin: '0 0 24px' }}>Obrigado pelo feedback. Nossa equipe vai analisar o problema.</p>
        <Button variant="primary" onClick={() => route('/')}>Voltar ao inicio</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 24px 80px', maxWidth: 640, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>Reportar um bug</h1>
        <p style={{ fontSize: 14, color: T.textMuted, margin: 0 }}>Descreva o problema encontrado. Informacoes do sistema sao capturadas automaticamente.</p>
      </div>

      {/* Form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Title */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
            Titulo <span style={{ color: T.danger }}>*</span>
          </label>
          <input
            value={title}
            onInput={(e: any) => setTitle(e.target.value)}
            placeholder="Ex: Botao de salvar nao funciona na pagina de configuracoes"
            style={inputStyle}
            onFocus={(e: any) => e.target.style.borderColor = T.primary}
            onBlur={(e: any) => e.target.style.borderColor = T.border}
          />
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
            Descricao <span style={{ color: T.danger }}>*</span>
          </label>
          <Textarea
            value={description}
            onInput={(e: any) => setDescription(e.target.value)}
            placeholder="Descreva o bug em detalhes. O que aconteceu? O que voce esperava que acontecesse?"
            rows={4}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }}
            onFocus={(e: any) => e.target.style.borderColor = T.primary}
            onBlur={(e: any) => e.target.style.borderColor = T.border}
          />
        </div>

        {/* Steps to reproduce */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
            Passos para reproduzir <span style={{ color: T.textMuted, fontWeight: 400 }}>(opcional)</span>
          </label>
          <Textarea
            value={steps}
            onInput={(e: any) => setSteps(e.target.value)}
            placeholder={"1. Abrir a pagina X\n2. Clicar no botao Y\n3. Observar o erro Z"}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
            onFocus={(e: any) => e.target.style.borderColor = T.primary}
            onBlur={(e: any) => e.target.style.borderColor = T.border}
          />
        </div>

        {/* Severity */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>Severidade</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {SEVERITY_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                variant={severity === opt.value ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setSeverity(opt.value)}
                style={{
                  flex: 1,
                  background: severity === opt.value ? `${SEVERITY_COLORS[opt.value]}20` : undefined,
                  color: severity === opt.value ? SEVERITY_COLORS[opt.value] : undefined,
                }}
              >{opt.label}</Button>
            ))}
          </div>
        </div>

        {/* Screenshot upload */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
            Screenshot <span style={{ color: T.textMuted, fontWeight: 400 }}>(opcional)</span>
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e: any) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop as any}
            style={{
              padding: screenshotPreview ? 8 : '24px 16px', borderRadius: 10, cursor: 'pointer',
              border: `2px dashed ${dragOver ? T.primary : T.border}`,
              background: dragOver ? T.primaryBg : T.bg,
              textAlign: 'center', transition: 'all 0.15s',
            }}
          >
            {screenshotPreview ? (
              <div style={{ position: 'relative' }}>
                <img src={screenshotPreview} alt="Screenshot" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }} />
                <Button variant="ghost" onClick={(e: any) => { e.stopPropagation(); setScreenshotFile(null); setScreenshotPreview(''); }}
                  style={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: '50%', background: T.bgOverlay, border: 'none', color: T.text, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </Button>
              </div>
            ) : (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 8px', display: 'block' }}>
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
                <div style={{ fontSize: 13, color: T.textMuted }}>Arraste uma imagem ou clique para selecionar</div>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e: any) => { const f = e.target.files?.[0]; if (f) handleScreenshot(f); }} />
        </div>

        {/* Auto-captured info */}
        <div style={{ padding: '14px 16px', background: T.bg, borderRadius: 10, border: `1px solid ${T.whiteAlpha8}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Informacoes capturadas automaticamente
          </div>
          <div style={{ display: 'grid', gap: 6, fontSize: 12, color: T.textSecondary }}>
            <div><span style={{ color: T.textDisabled }}>Navegador:</span> {autoInfo.browser.slice(0, 80)}...</div>
            <div><span style={{ color: T.textDisabled }}>Pagina:</span> {autoInfo.url}</div>
            <div><span style={{ color: T.textDisabled }}>Plano:</span> {autoInfo.plan || <Spinner size="sm" inline />}</div>
            <div><span style={{ color: T.textDisabled }}>Resolucao:</span> {autoInfo.resolution}</div>
            <div><span style={{ color: T.textDisabled }}>Timestamp:</span> {new Date(autoInfo.timestamp).toLocaleString('pt-BR')}</div>
            {autoInfo.consoleErrors.length > 0 && (
              <div><span style={{ color: T.textDisabled }}>Erros no console:</span> {autoInfo.consoleErrors.length} capturado(s)</div>
            )}
          </div>
        </div>

        {error && <div style={{ color: T.danger, fontSize: 13 }}>{error}</div>}

        {/* Submit */}
        <Button variant="primary" onClick={handleSubmit} disabled={submitting} loading={submitting} fullWidth size="lg">
          {submitting ? 'Enviando...' : 'Enviar bug report'}
        </Button>
      </div>
    </div>
  );
}
